import { MINUTES_PER_DAY, addCalendarDays, nextSendableMoment, } from './appointments.js';
import { canTransition } from './state/transition.js';
/**
 * Messaging and notifications. §6.17 `MSG-001` to `MSG-005a`; §6.33 `OPS-140` to `OPS-143`.
 *
 * > `MSG-001` `MUST` Channels: **email and in-app in v1. WhatsApp and SMS at v3.**
 *
 * > `MSG-002` `MUST` Templated, localised, tenant-brandable messages for: order confirmation,
 * > deposit receipt, job status change, delay notification, ready-for-collection, uncollected
 * > reminder, recall, appointment reminder, warranty expiry, storefront order updates.
 *
 * > `MSG-003` `MUST` **Message content must not include health data by default.** "Your order is
 * > ready" is safe; prescription values in an SMS are not. Templates that would embed Rx data
 * > require explicit tenant opt-in with a warning.
 *
 * > `MSG-005a` `MUST` **Staff and owner notifications**, distinct from customer messaging: job
 * > overdue, stock out, till variance above threshold, dispute opened, subscription past due, lab
 * > query unanswered, AI quota exhausted, reservation expired.
 *
 * ---
 *
 * **Customer messages and staff notifications are two things, and the requirement says so.**
 * `MSG-005a` calls staff notifications "distinct from customer messaging", and they differ in every
 * way that matters: a staff notification needs no consent, no quiet hours and no template, is never
 * health data about the person reading it, and is read in the product rather than delivered to an
 * address. Two state machines rather than one with unreachable arms.
 *
 * **Nothing here sends.** `MSG-001a` records why: `PK-021` makes WhatsApp the primary customer
 * channel in Pakistan and shop customers largely do not use email, so v1's mitigation is "printed
 * and downloadable artifacts the shop sends themselves". This module decides *whether* and *when* a
 * message may go, and what it says; a transport arrives with the channel.
 */
/** `MSG-002`'s own list, in its order. */
export const MESSAGE_KINDS = [
    'order_confirmation',
    'deposit_receipt',
    'job_status_change',
    'delay_notification',
    'ready_for_collection',
    'uncollected_reminder',
    'recall',
    'appointment_reminder',
    'warranty_expiry',
    /**
     * `MSG-002` names it and `V1-SPEC.md` §2 puts the public storefront out of v1, so nothing produces
     * one yet. Registered rather than dropped: a template a shop writes now is a template that works
     * the day the storefront ships, and leaving the kind out would make that an enumeration change.
     */
    'storefront_order_update',
];
/**
 * The channels a customer message may go out on in this release.
 *
 * `MESSAGE_CHANNELS` in `enums.ts` is Appendix E's full registry, which includes `whatsapp`, `sms`
 * and `push`; this is the subset `MSG-001` admits in v1. A consent row may name any of the five,
 * because a shop records consent today for a channel that ships later and throwing it away would be
 * a consent the customer gave and the product lost.
 */
export const V1_MESSAGE_CHANNELS = ['email', 'in_app'];
export const MESSAGE_STATUSES = [
    /** Accepted, with a send time already decided (`OPS-140`). Nothing has been handed to a channel. */
    'queued',
    /** Handed to a channel and not yet acknowledged. The only state a retry may leave. */
    'sending',
    /** The channel accepted it. `MSG-005`: not the same as delivered, and the difference matters. */
    'sent',
    /** The channel says it arrived. `MSG-005`'s delivery status. */
    'delivered',
    /**
     * `OPS-142`: "Bounced messages and invalid numbers surface in the work queue and mark the
     * contact." Distinct from `failed`, because a bounce is about the address and a failure is about
     * the attempt: one is retried and the other is not.
     */
    'bounced',
    /** The attempt failed and may be retried. `MSG-005`: a silent failure means an uncollected job. */
    'failed',
    /**
     * `MSG-004`: consent or suppression said no, checked again at the send. Recorded rather than
     * deleted, because "we did not message this customer and here is why" is the only answer to the
     * question anybody asks.
     */
    'suppressed',
    /** The reason for the message went away: the order was voided, the job collected. */
    'cancelled',
];
/**
 * Total, as `state/transition.ts` asks.
 *
 * `failed -> sending` is the retry, and `bounced` has no edge back: a bounce is the address being
 * wrong, so retrying it is sending to the same wrong address. `sent -> failed` exists because a
 * channel can accept a message and then report it undeliverable, which `MSG-005` is about.
 */
export const MESSAGE_TRANSITIONS = {
    queued: ['sending', 'suppressed', 'cancelled'],
    sending: ['sent', 'failed', 'bounced'],
    sent: ['delivered', 'bounced', 'failed'],
    delivered: [],
    bounced: [],
    failed: ['sending', 'cancelled'],
    suppressed: [],
    cancelled: [],
};
export function canMessageTransition(from, to) {
    return canTransition(MESSAGE_TRANSITIONS, from, to);
}
/**
 * Why a queued message was not sent, stored on the row. `MSG-004`'s two refusals first.
 *
 * The last two are the other things that can change while a message waits, and each is a different
 * fact about a different subject, so none of them may be recorded as one of the others:
 *
 * - `rx_opt_in_withdrawn` is `MSG-003`. The body was rendered while the shop allowed prescription
 *   values in messages and the shop has since said stop. The customer withdrew nothing, so writing
 *   `suppressed` here would put the shop's decision on the customer's record.
 * - `customer_erased` is `CUS-006`. `enqueue` refuses an erased customer outright, and a customer
 *   erased after the row was queued is the same refusal arriving later.
 */
export const MESSAGE_SUPPRESSION_REASONS = [
    'suppressed',
    'no_consent',
    'rx_opt_in_withdrawn',
    'customer_erased',
];
/** `MSG-005a`'s own list, in its order. */
export const STAFF_NOTIFICATION_KINDS = [
    'job_overdue',
    'stock_out',
    'till_variance_above_threshold',
    'dispute_opened',
    'subscription_past_due',
    'lab_query_unanswered',
    'ai_quota_exhausted',
    'reservation_expired',
];
/**
 * A staff notification is read or it is not. There is no delivery to track, because it is read in
 * the product, and no consent to check, because it is the shop's own operational signal.
 *
 * `dismissed` is separate from `read` on purpose: `MSG-005a`'s list is things somebody has to act
 * on, and "I have seen this" and "this no longer needs doing" are different answers. A shop that
 * cannot say the second ends up with a permanent list it stops reading.
 */
export const STAFF_NOTIFICATION_STATUSES = ['unread', 'read', 'dismissed'];
export const STAFF_NOTIFICATION_TRANSITIONS = {
    unread: ['read', 'dismissed'],
    // Back to unread is deliberate: a job that is still overdue tomorrow is still a notification.
    read: ['dismissed', 'unread'],
    dismissed: [],
};
export function canStaffNotificationTransition(from, to) {
    return canTransition(STAFF_NOTIFICATION_TRANSITIONS, from, to);
}
export class MessagingError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'MessagingError';
    }
}
const requireMinute = (name, value) => {
    if (!Number.isInteger(value) || value < 0 || value >= MINUTES_PER_DAY) {
        throw new MessagingError('QUIET_HOURS_INVALID', `${name} must be a whole number of minutes from local midnight, 0 to ${String(MINUTES_PER_DAY - 1)}.`);
    }
};
/** Whether a local time falls inside the quiet window, wrap-around included. */
export function isQuiet(quiet, minute) {
    requireMinute('quietHours.fromMinute', quiet.fromMinute);
    requireMinute('quietHours.toMinute', quiet.toMinute);
    requireMinute('minute', minute);
    // Equal ends would be a window of no length or of a whole day, and neither is a useful reading of
    // "a configurable window", so it is refused rather than guessed.
    if (quiet.fromMinute === quiet.toMinute) {
        throw new MessagingError('QUIET_HOURS_INVALID', 'Quiet hours cannot start and end at the same minute.');
    }
    return quiet.fromMinute < quiet.toMinute
        ? minute >= quiet.fromMinute && minute < quiet.toMinute
        : minute >= quiet.fromMinute || minute < quiet.toMinute;
}
/**
 * `OPS-140`: when an automated message may actually go.
 *
 * "Automated messages respect branch opening hours **and** a configurable window." Both, so this
 * applies `nextSendableMoment` for the opening hours and then steps past the quiet window, and
 * repeats until both agree, because leaving a quiet window can land on a closed day.
 *
 * `quiet` is optional and **has no default**. `OPS-140` names no window and §13 gives none for
 * Pakistan, so a shop that has set none is governed by its opening hours alone, which already
 * satisfies "nothing automated sends at 2am" for every shop that is shut at 2am. Picking a window
 * here would be this software deciding when a shop may talk to its customers.
 *
 * Always moves **later**. A message whose moment has passed goes at the next one it may; sending it
 * early would be sending it at a time the shop forbade.
 */
export function sendableAt(schedule, nominal, quiet) {
    let at = nextSendableMoment(schedule, nominal, 'later');
    if (quiet === undefined)
        return at;
    for (let attempts = 0; attempts <= 14; attempts += 1) {
        if (!isQuiet(quiet, at.minute))
            return at;
        // The first minute after the window. A window that wraps midnight ends on the next day.
        const wraps = quiet.fromMinute > quiet.toMinute;
        const afterQuiet = wraps && at.minute >= quiet.fromMinute
            ? { date: addCalendarDays(at.date, 1), minute: quiet.toMinute }
            : { date: at.date, minute: quiet.toMinute };
        at = nextSendableMoment(schedule, afterQuiet, 'later');
    }
    throw new MessagingError('NO_SENDABLE_MOMENT', 'No moment in the next fortnight is both inside opening hours and outside quiet hours. Check the branch calendar and the quiet window.');
}
// -----------------------------------------------------------------------------------------------
// MSG-003: health data in a message
// -----------------------------------------------------------------------------------------------
/**
 * `MSG-003`, as a gate rather than a note.
 *
 * "Message content must not include health data by default ... Templates that would embed Rx data
 * require explicit tenant opt-in with a warning." So a template that embeds Rx data **cannot be
 * active** unless the shop has opted in, and the opt-in is the shop's, not a developer's.
 *
 * The database holds the same rule as a constraint, so an opt-in withdrawn later cannot leave an
 * active template that reads a prescription. Which is the point: §8.1 makes a prescription value
 * special-category data, and a message is the one place it leaves the shop.
 */
export function templateMayBeActive(input) {
    return !input.embedsRxData || input.tenantOptedIn;
}
/**
 * The placeholders a template body may name. A template is rendered from these and nothing else, so
 * a body cannot reach a prescription value unless it says it does.
 *
 * Deliberately small and deliberately not a prescription. `MSG-003`'s safe example is "Your order is
 * ready", and everything here is of that kind: who, where, which order, when. The Rx placeholders
 * are the ones {@link templateMayBeActive} gates, and they are named separately so a body using one
 * is detectable rather than a judgement call.
 */
export const MESSAGE_PLACEHOLDERS = [
    'customer_name',
    'branch_name',
    'branch_phone',
    'order_number',
    'job_number',
    'amount',
    'balance',
    'promised_date',
    'appointment_date',
    'appointment_time',
    'shop_name',
];
/** The placeholders that make a template health data, and so need `MSG-003`'s opt-in. */
export const RX_PLACEHOLDERS = [
    'rx_sphere_od',
    'rx_sphere_os',
    'rx_cylinder_od',
    'rx_cylinder_os',
    'rx_axis_od',
    'rx_axis_os',
    'rx_add',
    'rx_summary',
];
const PLACEHOLDER = /\{\{\s*([a-z_]+)\s*\}\}/g;
/**
 * What a template body asks for.
 *
 * `embedsRxData` is **derived from the body**, not declared beside it. A boolean a person sets is a
 * boolean that can be wrong, and the one it would be wrong about is whether a prescription goes out
 * in an email.
 */
export function scanTemplate(body) {
    const found = [...body.matchAll(PLACEHOLDER)].map((m) => String(m[1]));
    const known = new Set([...MESSAGE_PLACEHOLDERS, ...RX_PLACEHOLDERS]);
    const placeholders = [...new Set(found)].sort();
    return {
        placeholders,
        unknown: placeholders.filter((name) => !known.has(name)),
        embedsRxData: placeholders.some((name) => RX_PLACEHOLDERS.includes(name)),
    };
}
/**
 * Fill a template. Every placeholder the body names must be supplied, because a message that says
 * "Your order {{order_number}} is ready" is worse than no message.
 */
export function renderTemplate(body, values) {
    const scan = scanTemplate(body);
    if (scan.unknown.length > 0) {
        throw new MessagingError('TEMPLATE_PLACEHOLDER_UNKNOWN', `${scan.unknown.join(', ')} is not a placeholder this product fills.`);
    }
    const missing = scan.placeholders.filter((name) => values[name] === undefined);
    if (missing.length > 0) {
        throw new MessagingError('TEMPLATE_VALUE_MISSING', `No value for ${missing.join(', ')}, so the message would go out with the braces in it.`);
    }
    return body.replace(PLACEHOLDER, (_whole, name) => values[name]);
}
//# sourceMappingURL=messaging.js.map